import { type GetStaticPropsResult, type GetStaticPathsResult } from 'next'
import dynamic from 'next/dynamic'
import { type ParsedUrlQuery } from 'querystring'

import { collectionPageModuleQuery } from '@data/sanity/queries/page'
import { type SanityCollectionPage } from '@data/sanity/queries/types/page'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getShopifyDomain } from '@lib/shopify/client'
import { getSanityClient } from '@lib/sanity/client'
import {
  defaultModuleQueryParameters,
  getAllSlugs,
  getCollectionPage,
} from '@lib/sanity/page'
import { type Locale, getAllLocales } from '@lib/language'
import {
  type BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { getReviews, Reviews } from '@lib/review'
import {
  type CustomStaticPropsContext,
  type StaticPathWithSlug,
  getPageUrl,
} from '@lib/routes'
import { getBreadcrumbListSchema } from '@lib/schema/breadcrumb'
import { getVideoObjectSchemas } from '@lib/schema/video'

import Layout from '@components/layout'
import Modules from '@modules/modules'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type CollectionPageProps = BasePageProps & {
  slug: string
  page: SanityCollectionPage
  site: SanitySiteFragment
  reviews?: Reviews
}

const CollectionPage = ({
  draftMode,
  locale,
  slug,
  page,
  site,
  reviews,
}: CollectionPageProps) => {
  const organizationUrl = site.schemaSettings?.organization?.url
  const shopPageUrl = getPageUrl(locale, 'shopPage')
  const collectionPageUrl = getPageUrl(locale, 'collection', slug)
  const breadcrumbListSchema = getBreadcrumbListSchema([
    {
      title: page.shopPage?.seo?.metaTitle,
      url: `${organizationUrl}${shopPageUrl}`,
    },
    {
      title: page.seo?.metaTitle ?? page.title,
      url: `${organizationUrl}${collectionPageUrl}`,
    },
  ])
  const videoObjectSchemas = getVideoObjectSchemas(page)
  const schemas = [breadcrumbListSchema, ...videoObjectSchemas]

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && <Modules modules={page.modules} reviews={reviews} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          reviews={reviews}
          query={collectionPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            collectionSlug: slug,
          }}
        />
      )}
    </Layout>
  )
}

interface StaticCollectionPageParams extends ParsedUrlQuery {
  slug: string
}

export async function getStaticProps(
  context: CustomStaticPropsContext<StaticCollectionPageParams>,
): Promise<GetStaticPropsResult<CollectionPageProps>> {
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const slug = context.params?.slug ?? ''
  const collectionPage = await getCollectionPage(
    sanityClient,
    context.locale,
    slug,
  )

  if (!collectionPage.page) {
    return {
      notFound: true,
    }
  }

  // Get Stamped.io reviews
  const shopifyDomain = getShopifyDomain(context.locale)
  const reviews = await getReviews(
    collectionPage.page.modules ?? [],
    shopifyDomain,
    collectionPage.site.generalSettings.stampedApiKey,
  )

  return {
    props: {
      ...getPagePropsFromContext(context),
      slug,
      page: getNonNullValues(collectionPage.page),
      site: getNonNullValues(collectionPage.site),
      reviews,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticCollectionPageParams>
> {
  if (process.env.NEXT_PUBLIC_APP_ENV === 'preview') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  const sanityClient = getSanityClient()
  const allCollections = await getAllSlugs(sanityClient, 'collection')
  const allLocales = getAllLocales()
  const paths: StaticPathWithSlug[] = allCollections
    .map((collection) => ({
      ...collection,
      slug: collection.slug.current,
      locale: collection.locale as Locale,
    }))
    .filter((page) => allLocales.includes(page.locale))
    .filter((page) => !!page.slug)
    .map((page) => ({
      params: {
        slug: page.slug,
      },
      locale: page.locale,
    }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export default CollectionPage
